/* Video Carousel Component
 * Handles inline video playback for YouTube, Vimeo, and native HTML5 videos
 */

class VideoCarousel {
  constructor(section) {
    this.section = section;
    this.videoCards = section.querySelectorAll('[data-video-card]');
    this.carouselWrapper = section.querySelector('.carousel__wrapper');
    this.activeVideo = null;

    this.init();
  }

  init() {

    this.videoCards.forEach((card) => {
      const playButton = card.querySelector('.video-card__play-button');
      if (playButton) {
        playButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.playVideo(card);
        });

        playButton.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.playVideo(card);
          }
        });
      }
    });

    if (this.carouselWrapper) {
      const prevButton = this.carouselWrapper.querySelector('.carousel__button--prev');
      const nextButton = this.carouselWrapper.querySelector('.carousel__button--next');

      if (prevButton) {
        prevButton.addEventListener('click', () => this.pauseAllVideos());
      }
      if (nextButton) {
        nextButton.addEventListener('click', () => this.pauseAllVideos());
      }
    }

    // Note: Swipe support is handled by the Carousel class that manages this video carousel
    // No additional swipe handler needed here since videos are displayed within a carousel wrapper
  }

  playVideo(card) {
    this.pauseAllVideos();

    const thumbnail = card.querySelector('[data-video-thumbnail]');
    const player = card.querySelector('[data-video-player]');
    const playButton = card.querySelector('.video-card__play-button');

    if (!player) return;

    thumbnail.style.display = 'none';
    player.style.display = 'block';

    if (playButton) {
      playButton.setAttribute('aria-label', 'Video playing');
      playButton.style.display = 'none';
    }

    // Show close button and hide username
    this.showCloseButton(card);
    this.hideUsername(card);

    const iframe = player.querySelector('iframe');
    const video = player.querySelector('video');

    if (iframe) {
      const videoType = iframe.dataset.videoType;
      const videoId = iframe.dataset.videoId;

      if (videoType === 'youtube') {
        // YouTube - use autoplay parameter
        iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
      } else if (videoType === 'vimeo') {
        // Vimeo - use autoplay parameter
        iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      }

      this.activeVideo = { card, type: 'iframe', element: iframe };
    } else if (video) {
      // Native HTML5 video
      video.play().catch((error) => {
        console.error('Error playing video:', error);
      });

      video.addEventListener('ended', () => {
        this.showThumbnail(card);
      });

      this.activeVideo = { card, type: 'video', element: video };
    }
  }

  pauseAllVideos() {
    this.videoCards.forEach((card) => {
      this.pauseVideo(card);
    });
    this.activeVideo = null;
  }

  pauseVideo(card) {
    const player = card.querySelector('[data-video-player]');
    if (!player) return;

    const iframe = player.querySelector('iframe');
    const video = player.querySelector('video');

    if (iframe) {
      // Stop iframe by removing src (works for both YouTube and Vimeo)
      iframe.src = '';
    } else if (video) {
      video.pause();
    }

    this.showThumbnail(card);
  }

  showThumbnail(card) {
    const thumbnail = card.querySelector('[data-video-thumbnail]');
    const player = card.querySelector('[data-video-player]');
    const playButton = card.querySelector('.video-card__play-button');

    if (thumbnail && player) {
      thumbnail.style.display = 'block';
      player.style.display = 'none';
    }

    if (playButton) {
      playButton.setAttribute('aria-label', 'Play video');
      playButton.style.display = '';
    }

    // Hide close button and show username
    this.hideCloseButton(card);
    this.showUsername(card);
  }

  hideUsername(card) {
    const videoCard = card.closest('.video-card') || card.closest('.instagram-card');
    if (videoCard) {
      const username = videoCard.querySelector('.instagram-card__username');
      if (username) {
        username.style.opacity = '0';
        username.style.pointerEvents = 'none';
      }
    }
  }

  showUsername(card) {
    const videoCard = card.closest('.video-card') || card.closest('.instagram-card');
    if (videoCard) {
      const username = videoCard.querySelector('.instagram-card__username');
      if (username) {
        username.style.opacity = '';
        username.style.pointerEvents = '';
      }
    }
  }

  showCloseButton(card) {
    let closeButton = card.querySelector('.video-card__close-button');

    // Create close button if it doesn't exist
    if (!closeButton) {
      closeButton = document.createElement('button');
      closeButton.className = 'video-card__close-button';
      closeButton.setAttribute('type', 'button');
      closeButton.setAttribute('aria-label', 'Close video');
      closeButton.innerHTML = `
        <svg aria-hidden="true" focusable="false" class="icon icon-close" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.showThumbnail(card);
        this.pauseVideo(card);
      });

      const player = card.querySelector('[data-video-player]');
      if (player) {
        player.appendChild(closeButton);
      }
    }

    closeButton.style.display = 'block';
  }

  hideCloseButton(card) {
    const closeButton = card.querySelector('.video-card__close-button');
    if (closeButton) {
      closeButton.style.display = 'none';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const videoCarouselSections = document.querySelectorAll('.carousel-video, .carousel-instagram');
  videoCarouselSections.forEach((section) => {
    new VideoCarousel(section);
  });
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const videoSection = event.target.querySelector('.carousel-video');
    if (videoSection) {
      new VideoCarousel(videoSection);
    }

    const instagramSection = event.target.querySelector('.carousel-instagram');
    if (instagramSection) {
      new VideoCarousel(instagramSection);
    }
  });
}
